/**
 * Composability Graph Generator
 *
 * Computes the full dependency/composability graph from the action registry,
 * showing which actions can feed into which other actions based on typed
 * output→input compatibility.
 *
 * This is a derived data structure — it auto-updates when actions are added
 * or modified. Run `generateComposabilityGraph()` to recompute.
 *
 * @module gibbs/registry/composabilityGraph
 */

import { getAllActions } from './registry';
import { isTypeCompatible } from './types';

// ─── Graph Generation ───────────────────────────────────────────────────────

/**
 * @typedef {Object} GraphEdge
 * @property {string} source - Source action ID
 * @property {string} target - Target action ID
 * @property {string} outputName - Output name from source action
 * @property {string} inputName - Input name on target action
 * @property {string} outputType - WorkflowType of the output
 * @property {string} inputType - WorkflowType of the input
 */

/**
 * @typedef {Object} GraphNode
 * @property {string} id - Action ID
 * @property {string} name - Human-readable name
 * @property {string} category - Action category
 * @property {string} runtime - Runtime classification
 * @property {string} status - implemented | planned
 * @property {Array<{name: string, type: string}>} inputs
 * @property {Array<{name: string, type: string}>} outputs
 * @property {string[]} preconditions - Required workspace state check IDs
 * @property {string[]} postconditions - Guaranteed state after execution
 */

/**
 * @typedef {Object} ComposabilityGraph
 * @property {GraphNode[]} nodes
 * @property {GraphEdge[]} edges
 * @property {Object<string, {canFlowTo: string[], canReceiveFrom: string[]}>} adjacency
 * @property {Object<string, string[]>} categories - Category → action IDs
 * @property {Object} stats
 */

/**
 * Generate the full composability graph from the action registry.
 *
 * For every pair of actions (A, B), checks if any typed output of A
 * is compatible with any typed input of B (using isTypeCompatible).
 * Also considers postcondition→precondition connections.
 *
 * @returns {ComposabilityGraph}
 */
export function generateComposabilityGraph() {
    const actions = getAllActions();

    // ── Build nodes ──
    const nodes = actions.map((action) => ({
        id: action.id,
        name: action.name,
        category: action.category,
        runtime: action.runtime,
        status: action.status,
        inputs: (action.inputs || []).map((i) => ({ name: i.name, type: i.type })),
        outputs: (action.outputs || []).map((o) => ({ name: o.name, type: o.type })),
        preconditions: (action.preconditions || []).map((p) => p.check),
        postconditions: (action.postconditions || []).map((p) => p.guarantee),
    }));

    // ── Compute edges ──
    const edges = [];
    const edgeSet = new Set(); // dedupe

    for (const source of actions) {
        for (const target of actions) {
            if (source.id === target.id) continue;

            // Check typed output→input compatibility
            for (const output of (source.outputs || [])) {
                for (const input of (target.inputs || [])) {
                    // Skip primitive types (string, number, boolean, object)
                    // — they don't represent meaningful data flow
                    if (isPrimitive(output.type) || isPrimitive(input.type)) continue;

                    if (isTypeCompatible(output.type, input.type)) {
                        const key = `${source.id}|${target.id}|${output.name}|${input.name}`;
                        if (!edgeSet.has(key)) {
                            edgeSet.add(key);
                            edges.push({
                                source: source.id,
                                target: target.id,
                                outputName: output.name,
                                inputName: input.name,
                                outputType: output.type,
                                inputType: input.type,
                            });
                        }
                    }
                }
            }
        }
    }

    // ── Build adjacency index ──
    const adjacency = {};
    for (const node of nodes) {
        adjacency[node.id] = { canFlowTo: [], canReceiveFrom: [] };
    }
    for (const edge of edges) {
        if (!adjacency[edge.source].canFlowTo.includes(edge.target)) {
            adjacency[edge.source].canFlowTo.push(edge.target);
        }
        if (!adjacency[edge.target].canReceiveFrom.includes(edge.source)) {
            adjacency[edge.target].canReceiveFrom.push(edge.source);
        }
    }

    // ── Build category index ──
    const categories = {};
    for (const node of nodes) {
        if (!categories[node.category]) categories[node.category] = [];
        categories[node.category].push(node.id);
    }

    // ── Compute postcondition→precondition links ──
    const stateLinks = [];
    for (const source of actions) {
        for (const target of actions) {
            if (source.id === target.id) continue;
            for (const post of (source.postconditions || [])) {
                for (const pre of (target.preconditions || [])) {
                    if (post.guarantee === pre.check) {
                        stateLinks.push({
                            source: source.id,
                            target: target.id,
                            guarantee: post.guarantee,
                            description: pre.description,
                        });
                    }
                }
            }
        }
    }

    // ── Compute stats ──
    const stats = computeStats(nodes, edges, adjacency, stateLinks);

    return {
        nodes, edges, adjacency, categories, stateLinks, stats,
    };
}

// ─── Stats ──────────────────────────────────────────────────────────────────

function computeStats(nodes, edges, adjacency, stateLinks) {
    const outDegrees = nodes.map((n) => ({
        id: n.id,
        name: n.name,
        degree: adjacency[n.id].canFlowTo.length,
    })).sort((a, b) => b.degree - a.degree);

    const inDegrees = nodes.map((n) => ({
        id: n.id,
        name: n.name,
        degree: adjacency[n.id].canReceiveFrom.length,
    })).sort((a, b) => b.degree - a.degree);

    const isolated = nodes.filter((n) => adjacency[n.id].canFlowTo.length === 0
        && adjacency[n.id].canReceiveFrom.length === 0);

    const sourceOnly = nodes.filter((n) => adjacency[n.id].canFlowTo.length > 0
        && adjacency[n.id].canReceiveFrom.length === 0);

    const sinkOnly = nodes.filter((n) => adjacency[n.id].canFlowTo.length === 0
        && adjacency[n.id].canReceiveFrom.length > 0);

    // Type frequency in edges
    const typeFrequency = {};
    for (const edge of edges) {
        typeFrequency[edge.outputType] = (typeFrequency[edge.outputType] || 0) + 1;
    }

    return {
        totalActions: nodes.length,
        implementedActions: nodes.filter((n) => n.status === 'implemented').length,
        plannedActions: nodes.filter((n) => n.status === 'planned').length,
        totalEdges: edges.length,
        totalStateLinks: stateLinks.length,
        avgOutDegree: (edges.length / nodes.length).toFixed(1),
        mostConnectedProducers: outDegrees.slice(0, 10),
        mostConnectedConsumers: inDegrees.slice(0, 10),
        sourceActions: sourceOnly.map((n) => n.id),
        sinkActions: sinkOnly.map((n) => n.id),
        isolatedActions: isolated.map((n) => n.id),
        typeFrequency: Object.entries(typeFrequency)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => ({ type, count })),
    };
}

// ─── Markdown Export ────────────────────────────────────────────────────────

/**
 * Generate a human-readable Markdown summary of the composability graph.
 *
 * @param {ComposabilityGraph} graph
 * @returns {string} Markdown text
 */
export function graphToMarkdown(graph) {
    const lines = [];

    lines.push('# BMaps Action Composability Graph');
    lines.push('');
    lines.push('> Auto-generated from the Gibbs action registry. Regenerate with `generateComposabilityGraph()`.');
    lines.push('');

    // ── Stats ──
    lines.push('## Summary Statistics');
    lines.push('');
    lines.push('| Metric | Value |');
    lines.push('|--------|-------|');
    lines.push(`| Total actions | ${graph.stats.totalActions} |`);
    lines.push(`| Implemented | ${graph.stats.implementedActions} |`);
    lines.push(`| Planned | ${graph.stats.plannedActions} |`);
    lines.push(`| Type-based connections | ${graph.stats.totalEdges} |`);
    lines.push(`| State-based connections | ${graph.stats.totalStateLinks} |`);
    lines.push(`| Avg outgoing connections | ${graph.stats.avgOutDegree} |`);
    lines.push('');

    // ── Most connected ──
    lines.push('## Most Connected Actions');
    lines.push('');
    lines.push('### Top Producers (most downstream connections)');
    lines.push('');
    lines.push('| Action | Outgoing |');
    lines.push('|--------|----------|');
    for (const a of graph.stats.mostConnectedProducers) {
        lines.push(`| ${a.name} (\`${a.id}\`) | ${a.degree} |`);
    }
    lines.push('');

    lines.push('### Top Consumers (most upstream connections)');
    lines.push('');
    lines.push('| Action | Incoming |');
    lines.push('|--------|----------|');
    for (const a of graph.stats.mostConnectedConsumers) {
        lines.push(`| ${a.name} (\`${a.id}\`) | ${a.degree} |`);
    }
    lines.push('');

    // ── Type frequency ──
    lines.push('## Data Types Driving Connections');
    lines.push('');
    lines.push('| Type | Connections |');
    lines.push('|------|------------|');
    for (const t of graph.stats.typeFrequency) {
        lines.push(`| \`${t.type}\` | ${t.count} |`);
    }
    lines.push('');

    // ── Source / Sink / Isolated ──
    lines.push('## Workflow Entry Points (sources — no typed inputs from other actions)');
    lines.push('');
    for (const id of graph.stats.sourceActions) {
        const node = graph.nodes.find((n) => n.id === id);
        lines.push(`- **${node.name}** (\`${id}\`) → can flow to: ${graph.adjacency[id].canFlowTo.length} actions`);
    }
    lines.push('');

    lines.push('## Workflow Terminal Actions (sinks — no typed outputs consumed)');
    lines.push('');
    for (const id of graph.stats.sinkActions) {
        const node = graph.nodes.find((n) => n.id === id);
        lines.push(`- **${node.name}** (\`${id}\`) ← receives from: ${graph.adjacency[id].canReceiveFrom.length} actions`);
    }
    lines.push('');

    if (graph.stats.isolatedActions.length > 0) {
        lines.push('## Isolated Actions (no typed connections)');
        lines.push('');
        for (const id of graph.stats.isolatedActions) {
            const node = graph.nodes.find((n) => n.id === id);
            lines.push(`- **${node.name}** (\`${id}\`) — ${node.category}`);
        }
        lines.push('');
    }

    // ── Per-category adjacency ──
    lines.push('## Connections by Category');
    lines.push('');
    for (const [category, actionIds] of Object.entries(graph.categories)) {
        lines.push(`### ${category}`);
        lines.push('');
        for (const id of actionIds) {
            const adj = graph.adjacency[id];
            const node = graph.nodes.find((n) => n.id === id);
            const outputs = node.outputs.map((o) => `\`${o.type}\``).join(', ') || '_none_';
            const flowTo = adj.canFlowTo.length > 0
                ? adj.canFlowTo.map((tid) => `\`${tid}\``).join(', ')
                : '_terminal_';
            lines.push(`**${node.name}** (\`${id}\`)`);
            lines.push(`- Produces: ${outputs}`);
            lines.push(`- Flows to: ${flowTo}`);
            lines.push('');
        }
    }

    // ── State links ──
    if (graph.stateLinks.length > 0) {
        lines.push('## State-Based Dependencies (postcondition → precondition)');
        lines.push('');
        lines.push('These connections represent workspace state requirements — action B requires');
        lines.push('a state that action A guarantees, independent of typed data flow.');
        lines.push('');
        lines.push('| Source | Guarantee | Target |');
        lines.push('|--------|-----------|--------|');
        const seen = new Set();
        for (const link of graph.stateLinks) {
            const key = `${link.source}|${link.guarantee}|${link.target}`;
            if (seen.has(key)) continue;
            seen.add(key);
            lines.push(`| \`${link.source}\` | \`${link.guarantee}\` | \`${link.target}\` |`);
        }
        lines.push('');
    }

    return lines.join('\n');
}

// ─── JSON Export ────────────────────────────────────────────────────────────

/**
 * Export the graph as a JSON string.
 *
 * @param {ComposabilityGraph} graph
 * @param {boolean} [pretty=true] - Pretty-print with indentation
 * @returns {string}
 */
export function graphToJSON(graph, pretty = true) {
    return JSON.stringify(graph, null, pretty ? 2 : 0);
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function isPrimitive(type) {
    return ['string', 'number', 'boolean', 'object', 'function'].includes(type);
}

/**
 * Find all possible workflow paths from a source action to a target action.
 * Uses BFS with a max depth to avoid combinatorial explosion.
 *
 * @param {ComposabilityGraph} graph
 * @param {string} sourceId - Starting action ID
 * @param {string} targetId - Destination action ID
 * @param {number} [maxDepth=6] - Maximum path length
 * @returns {string[][]} Array of paths (each path is an array of action IDs)
 */
export function findPaths(graph, sourceId, targetId, maxDepth = 6) {
    const paths = [];
    const queue = [[sourceId]];

    while (queue.length > 0) {
        const path = queue.shift();
        const current = path[path.length - 1];

        if (current === targetId && path.length > 1) {
            paths.push(path);
            continue;
        }

        if (path.length >= maxDepth) continue;

        const neighbors = graph.adjacency[current]?.canFlowTo || [];
        for (const next of neighbors) {
            if (!path.includes(next)) {
                queue.push([...path, next]);
            }
        }
    }

    return paths;
}

/**
 * Get all actions reachable from a given starting action.
 *
 * @param {ComposabilityGraph} graph
 * @param {string} startId - Starting action ID
 * @returns {Set<string>} Set of reachable action IDs
 */
export function getReachable(graph, startId) {
    const visited = new Set();
    const queue = [startId];

    while (queue.length > 0) {
        const current = queue.shift();
        if (visited.has(current)) continue;
        visited.add(current);

        const neighbors = graph.adjacency[current]?.canFlowTo || [];
        for (const next of neighbors) {
            if (!visited.has(next)) queue.push(next);
        }
    }

    visited.delete(startId);
    return visited;
}
